import { useEffect } from 'react';
import {
  Column,
  useExpanded,
  useFilters,
  usePagination,
  useSortBy,
  useTable,
} from 'react-table';

import { InitialSorting } from 'types';

import { useAppDispatch, useAppSelector } from 'redux/hooks';
import {
  getCurrentPage,
  getSize,
  getTotalPages,
  setTableCurrentPage,
  setTableSize,
  setTableSorting,
} from 'redux/slices/tableConfig';

const useTableComponent = (
  columns: readonly Column<{}>[],
  data: readonly {}[],
  initialSorting: InitialSorting[] | undefined = undefined,
  hasApiSorting: boolean = false,
  isNested: boolean = false
) => {
  const totalPages = useAppSelector(getTotalPages);
  const indexPage = useAppSelector(getCurrentPage);
  const initialSize = useAppSelector(getSize);
  const dispatch = useAppDispatch();

  const tableInstance = useTable(
    {
      columns,
      data,
      initialState: {
        sortBy: initialSorting,
        pageIndex: isNested ? indexPage : 0,
        pageSize: initialSize,
      },
      manualPagination: true,
      pageCount: totalPages,
      autoResetPage: false,
      autoResetSortBy: false,
      manualSortBy: hasApiSorting,
      autoResetExpanded: false,
      // 3-click cycle (asc -> desc -> no sort/no arrow, back to whatever
      // order the data had before sorting) — react-table's own default. The sortBy effect below now also dispatches
      // on the empty (cleared) state, so Redux's stored sort actually clears
      // too instead of silently keeping the last non-empty value.
    },
    useFilters,
    useSortBy,
    useExpanded,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    setFilter,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    visibleColumns,
    toggleAllRowsExpanded,
    state: { pageIndex, pageSize, sortBy },
  } = tableInstance;

  useEffect(() => {
    gotoPage(indexPage);
  }, [indexPage]);

  useEffect(() => {
    dispatch(setTableCurrentPage(pageIndex));
    toggleAllRowsExpanded(false);
  }, [pageIndex]);

  useEffect(() => {
    dispatch(setTableSize(pageSize));
  }, [pageSize]);

  useEffect(() => {
    dispatch(setTableSorting(sortBy.length ? sortBy[0] : undefined));
  }, [sortBy]);

  return {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    setFilter,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    visibleColumns,
    pageIndex,
    pageSize,
  };
};

export { useTableComponent };
